import { earnedAchievementsFromGrants, type EarnedAchievement } from "@dailywars/core/achievements";
import { getAchievementProfileSummary, getPlayerByDisplayName } from "@dailywars/core/db";
import { createServerFn } from "@tanstack/react-start";
import * as z from "zod";

import { setRouteAction } from "#/lib/requestLogger.server";
import type { ProfileActivityData } from "#/server/game/profileActivity";
import { buildProfileActivityForPlayer } from "#/server/game/profileActivity.server";
import { shapePublicPlayerProfile, type PlayerProfile } from "#/server/playerProfileShape";

export type PublicProfile = {
  profile: PlayerProfile;
  activity: ProfileActivityData;
  achievements: EarnedAchievement[];
  earnedAchievementCount: number;
};

export const loadPublicProfile = createServerFn({ method: "GET" })
  .validator(z.object({ name: z.string() }))
  .handler(async ({ data }): Promise<PublicProfile | null> => {
    setRouteAction("load_public_profile");
    const player = await getPlayerByDisplayName(data.name);
    if (!player) return null;
    const [activity, achievementSummary] = await Promise.all([
      buildProfileActivityForPlayer(player.playerId),
      getAchievementProfileSummary({ playerId: player.playerId, latestLimit: 5 }),
    ]);
    return {
      profile: shapePublicPlayerProfile(player),
      activity,
      achievements: earnedAchievementsFromGrants(achievementSummary.latestGrants),
      earnedAchievementCount: achievementSummary.earnedCount,
    };
  });
